// useWorkflowSession — owns the WorkflowSession state for v2 and persists
// changes to Postgres (workflow_conversations + workflow_steps).
//
// The reducer is pure. This hook applies it synchronously and schedules the
// matching DB writes. Status changes write immediately; text-heavy fields
// debounce 400ms.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  persistTargetsForAction,
  workflowSessionReducer,
  type WorkflowAction,
} from '@/lib/workflowSessionReducer';
import {
  type StepState,
  type WorkflowSession,
  type WorkflowCadence,
  type WorkflowPhase,
  type StepPhase,
} from '@/lib/workflowStreamProtocol';

interface UseWorkflowSessionResult {
  session: WorkflowSession | null;
  isLoading: boolean;
  error: string | null;
  dispatch: (action: WorkflowAction) => void;
  /** Manually flush pending debounced writes (e.g. before navigating away). */
  flush: () => Promise<void>;
}

// ── Row ⇄ state mappers ───────────────────────────────────────────────

// deno-lint-ignore no-explicit-any
type Json = any;

interface WorkflowConversationRow {
  id: string;
  user_id: string;
  org_id: string | null;
  name: string | null;
  initial_goal: string | null;
  phase: WorkflowPhase;
  active_step_id: string | null;
  cadence: string | null;
  selected_tool: string | null;
  stream_messages_json: Json;
  started_at: string;
  completed_at: string | null;
}

interface WorkflowStepRow {
  id: string;
  workflow_conversation_id: string;
  position: number;
  step_name: string;
  description: string | null;
  type: 'manual' | 'ai_assisted' | 'other';
  recommended_tool: string | null;
  estimated_duration: string | null;
  inputs: Json;
  outputs: Json;
  handoff_instruction: string | null;
  phase: StepPhase;
  prompt_draft_json: Json;
  prompt_history_json: Json;
  pasted_output_text: string | null;
  output_meta_json: Json;
  analysis_json: Json;
  decisions_json: Json;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  submission_id: string | null;
  score_id: string | null;
}

function rowToStep(row: WorkflowStepRow): StepState {
  return {
    id: row.id,
    position: row.position,
    stepName: row.step_name,
    description: row.description ?? '',
    type: row.type,
    recommendedTool: row.recommended_tool ?? undefined,
    estimatedDuration: row.estimated_duration ?? undefined,
    inputs: Array.isArray(row.inputs) ? row.inputs : [],
    outputs: Array.isArray(row.outputs) ? row.outputs : [],
    handoffInstruction: row.handoff_instruction ?? undefined,
    phase: row.phase,
    promptDraft: row.prompt_draft_json ?? undefined,
    promptHistory: Array.isArray(row.prompt_history_json) ? row.prompt_history_json : [],
    pastedOutput: row.pasted_output_text
      ? { text: row.pasted_output_text, meta: row.output_meta_json ?? undefined }
      : undefined,
    analysis: row.analysis_json ?? undefined,
    decisions: Array.isArray(row.decisions_json) ? row.decisions_json : [],
    startedAt: row.started_at ?? undefined,
    completedAt: row.completed_at ?? undefined,
    createdAt: row.created_at,
    scoreId: row.score_id ?? undefined,
    submissionId: row.submission_id ?? undefined,
  };
}

function stepToRow(step: StepState, workflowId: string): Omit<WorkflowStepRow, 'created_at'> {
  return {
    id: step.id,
    workflow_conversation_id: workflowId,
    position: step.position,
    step_name: step.stepName,
    description: step.description,
    type: step.type,
    recommended_tool: step.recommendedTool ?? null,
    estimated_duration: step.estimatedDuration ?? null,
    inputs: step.inputs,
    outputs: step.outputs,
    handoff_instruction: step.handoffInstruction ?? null,
    phase: step.phase,
    prompt_draft_json: step.promptDraft ?? null,
    prompt_history_json: step.promptHistory,
    pasted_output_text: step.pastedOutput?.text ?? null,
    output_meta_json: step.pastedOutput?.meta ?? null,
    analysis_json: step.analysis ?? null,
    decisions_json: step.decisions,
    started_at: step.startedAt ?? null,
    completed_at: step.completedAt ?? null,
    submission_id: step.submissionId ?? null,
    score_id: step.scoreId ?? null,
  };
}

function rowToSession(row: WorkflowConversationRow, stepRows: WorkflowStepRow[]): WorkflowSession {
  return {
    id: row.id,
    userId: row.user_id,
    orgId: row.org_id,
    name: row.name,
    objective: row.initial_goal,
    cadence: (row.cadence as WorkflowCadence | null) ?? null,
    phase: row.phase,
    activeStepId: row.active_step_id,
    selectedTool: row.selected_tool,
    steps: stepRows.map(rowToStep).sort((a, b) => a.position - b.position),
    messages: Array.isArray(row.stream_messages_json) ? row.stream_messages_json : [],
    startedAt: row.started_at,
    completedAt: row.completed_at,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────

const DEBOUNCED_MS = 400;

export function useWorkflowSession(workflowId: string | null): UseWorkflowSessionResult {
  const [session, setSession] = useState<WorkflowSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Synchronous mirror so reducers can read the latest state before React commits.
  const sessionRef = useRef<WorkflowSession | null>(null);

  // Pending-write registry: keyed for coalescing.
  // 'workflow' key = the parent row; 'step:<id>' key = a step row.
  const pendingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingFlushers = useRef<Map<string, () => Promise<void>>>(new Map());

  // ── Hydrate from DB ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!workflowId) {
      setSession(null);
      sessionRef.current = null;
      return;
    }
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const wfResult: any = await (supabase as any)
          .from('workflow_conversations')
          .select('id, user_id, org_id, name, initial_goal, phase, active_step_id, cadence, selected_tool, stream_messages_json, started_at, completed_at, is_archived')
          .eq('id', workflowId)
          .single();

        if (wfResult.error) throw wfResult.error;

        if (wfResult.data?.is_archived) {
          throw new Error('This workflow has been archived.');
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stepsResult: any = await (supabase as any)
          .from('workflow_steps')
          .select('*')
          .eq('workflow_conversation_id', workflowId)
          .order('position', { ascending: true });

        if (stepsResult.error) throw stepsResult.error;
        if (cancelled) return;

        const next = rowToSession(wfResult.data as WorkflowConversationRow, (stepsResult.data ?? []) as WorkflowStepRow[]);
        sessionRef.current = next;
        setSession(next);
      } catch (err) {
        if (cancelled) return;
        console.error('[useWorkflowSession] hydrate error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load workflow');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  // ── Schedulers ─────────────────────────────────────────────────────

  const scheduleWrite = useCallback((key: string, debounceMs: number, doWrite: () => Promise<void>) => {
    // Coalesce: latest doWrite wins.
    pendingFlushers.current.set(key, doWrite);

    const prev = pendingTimers.current.get(key);
    if (prev) clearTimeout(prev);

    if (debounceMs === 0) {
      pendingTimers.current.delete(key);
      const fn = pendingFlushers.current.get(key);
      pendingFlushers.current.delete(key);
      if (fn) void fn();
      return;
    }

    const t = setTimeout(() => {
      pendingTimers.current.delete(key);
      const fn = pendingFlushers.current.get(key);
      pendingFlushers.current.delete(key);
      if (fn) void fn();
    }, debounceMs);
    pendingTimers.current.set(key, t);
  }, []);

  const writeWorkflowFields = useCallback(async (current: WorkflowSession) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await (supabase as any)
        .from('workflow_conversations')
        .update({
          name: current.name,
          initial_goal: current.objective,
          phase: current.phase,
          active_step_id: current.activeStepId,
          cadence: current.cadence,
          selected_tool: current.selectedTool,
          stream_messages_json: current.messages,
          completed_at: current.completedAt,
        })
        .eq('id', current.id);
      if (res.error) console.error('[useWorkflowSession] workflow update error:', res.error);
    } catch (err) {
      console.error('[useWorkflowSession] workflow update threw:', err);
    }
  }, []);

  const upsertStep = useCallback(async (workflowId: string, step: StepState) => {
    try {
      const row = stepToRow(step, workflowId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await (supabase as any)
        .from('workflow_steps')
        .upsert(row, { onConflict: 'id' });
      if (res.error) console.error('[useWorkflowSession] step upsert error:', res.error);
    } catch (err) {
      console.error('[useWorkflowSession] step upsert threw:', err);
    }
  }, []);

  const deleteStep = useCallback(async (stepId: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await (supabase as any).from('workflow_steps').delete().eq('id', stepId);
      if (res.error) console.error('[useWorkflowSession] step delete error:', res.error);
    } catch (err) {
      console.error('[useWorkflowSession] step delete threw:', err);
    }
  }, []);

  // ── Dispatch ────────────────────────────────────────────────────────

  const dispatch = useCallback((action: WorkflowAction) => {
    const prev = sessionRef.current;
    if (!prev) return; // no session yet

    const next = workflowSessionReducer(prev, action);
    sessionRef.current = next;
    setSession(next);

    // Special-case actions that touch many rows.
    // Must sequence: insert step rows BEFORE updating the workflow row, because
    // workflow_conversations.active_step_id FKs to workflow_steps.id.
    if (action.type === 'set_selected_tool') {
      // Propagates tool to all step rows + writes workflow selected_tool.
      scheduleWrite('tool-selection', 0, async () => {
        await Promise.all(next.steps.map(step => upsertStep(next.id, step)));
        await writeWorkflowFields(next);
      });
      return;
    }

    if (action.type === 'seed_from_proposal' || action.type === 'apply_plan_diff') {
      const prevIds = new Set(prev.steps.map(s => s.id));
      const nextIds = new Set(next.steps.map(s => s.id));
      const removed = [...prevIds].filter(id => !nextIds.has(id));
      scheduleWrite('seed-sequence', 0, async () => {
        await Promise.all(next.steps.map(step => upsertStep(next.id, step)));
        await Promise.all(removed.map(id => deleteStep(id)));
        await writeWorkflowFields(next);
      });
      return;
    }

    // Standard actions: persist based on hints.
    const targets = persistTargetsForAction(action);
    targets.forEach(target => {
      if (target.table === 'workflow_conversations') {
        scheduleWrite('workflow', target.debounceMs, () => writeWorkflowFields(next));
      } else {
        const step = next.steps.find(s => s.id === target.stepId);
        if (step) scheduleWrite(`step:${target.stepId}`, target.debounceMs, () => upsertStep(next.id, step));
      }
    });
  }, [scheduleWrite, writeWorkflowFields, upsertStep, deleteStep]);

  // ── Flush ──────────────────────────────────────────────────────────

  const flush = useCallback(async () => {
    const flushers = Array.from(pendingFlushers.current.entries());
    pendingTimers.current.forEach(t => clearTimeout(t));
    pendingTimers.current.clear();
    pendingFlushers.current.clear();
    await Promise.all(flushers.map(([, fn]) => fn()));
  }, []);

  // Flush pending writes on unmount.
  useEffect(() => () => { void flush(); }, [flush]);

  return useMemo(
    () => ({ session, isLoading, error, dispatch, flush }),
    [session, isLoading, error, dispatch, flush],
  );
}
